import BaseChart from "../../baseChart";
import { useMemo } from "react";
import type { EChartsOption } from "echarts";

interface AreaChartProps {
  data: any[];
  config: any;
  height: number;
  width: number;
}

// Helper function to format values based on currency code or other formatters
const formatValue = (value: number, formatter?: string): string => {
  if (!formatter) {
    return value.toLocaleString();
  }
  
  // Handle currency codes (e.g., 'USD', 'EUR', 'GBP')
  if (formatter.length === 3 && /^[A-Z]{3}$/.test(formatter)) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: formatter,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (error) {
      // If currency code is not recognized, fall back to basic formatting
      return `${formatter} ${value.toLocaleString()}`;
    }
  }
  
  // Handle percentage formatting
  if (formatter.toLowerCase() === 'percentage' || formatter === '%') {
    return `${value.toFixed(2)}%`;
  }
  
  // Default formatting with the formatter as prefix/suffix
  return `${formatter} ${value.toLocaleString()}`;
};

const AreaChart = ({ data, config, height, width }: AreaChartProps) => {
  const chartOptions: EChartsOption = useMemo(() => {
    if (!data || !config || !config.values || data.length === 0) {
      return {
        xAxis: {
          type: "category",
          data: [],
        },
        yAxis: {
          type: "value",
        },
        series: [],
      };
    }

    // Generate series based on config.values
    const series = config.values.map((valueConfig: any, index: number) => {
      const isFirstSeries = index === 0;

      return {
        name: valueConfig.label,
        type: isFirstSeries ? "line" : "line",
        data: data.map((item) => item[valueConfig.accessor]),
        smooth: true,
        symbol: isFirstSeries ? "none" : "circle",
        symbolSize: 4,
        lineStyle: {
          color: valueConfig.color,
          width: 2,
        },
        itemStyle: {
          color: valueConfig.color,
        },
        areaStyle: isFirstSeries
          ? {
              color: valueConfig.color,
              opacity: 0.3,
            }
          : undefined,
        yAxisIndex: valueConfig.axisPosition === "left" ? 0 : 1,
      };
    });

    // Group values by axis position
    const axisGroups = config.values.reduce((groups: any, valueConfig: any) => {
      const position = valueConfig.axisPosition;
      if (!groups[position]) {
        groups[position] = [];
      }
      groups[position].push(valueConfig);
      return groups;
    }, {});

    // Generate y-axes based on axis positions
    const yAxes = Object.keys(axisGroups).map((position) => {
      // Get the first value config for this axis to determine formatter
      const firstValueConfig = axisGroups[position][0];
      const formatter = firstValueConfig?.value_formatter;
      
      return {
        type: "value" as const,
        name: config.yAxis?.label || "",
        position: position === "left" ? ("left" as const) : ("right" as const),
        nameTextStyle: {
          fontSize: 12,
        },
        axisLabel: {
          fontSize: 12,
          formatter: (value: number) => formatValue(value, formatter),
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: "#F3F4F6",
            type: "dashed" as const,
          },
        },
      };
    });

    // Ensure we have at least one y-axis
    if (yAxes.length === 0) {
      yAxes.push({
        type: "value" as const,
        name: config.yAxis?.label || "",
        position: "left" as const,
        nameTextStyle: {
          fontSize: 12,
        },
        axisLabel: {
          fontSize: 12,
          formatter: (value: number) => formatValue(value),
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: "#F3F4F6",
            type: "dashed" as const,
          },
        },
      });
    }

    return {
      xAxis: {
        type: "category",
        data: data.map((item) => item[config.category]),
        name: config.xAxis?.label || "",
        nameTextStyle: {
          fontSize: 12,
        },
        axisLabel: {
          fontSize: 12,
          rotate: 0,
        },
      },
      yAxis: yAxes,
      series,
      legend: {
        show: true,
        top: 10,
        itemGap: 20,
        itemWidth: 8,
        itemHeight: 8,
        textStyle: {
          fontSize: 12,
        },
      },
      grid: {
        top: 60,
        left: 10,
        right: 10,
        bottom: 10,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
        },
        formatter: (params: any) => {
          if (!Array.isArray(params)) {
            params = [params];
          }
          
          let tooltip = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0]?.axisValueLabel}</div>`;
          
          params.forEach((param: any) => {
            const seriesConfig = config.values.find((v: any) => v.label === param.seriesName);
            const formatter = seriesConfig?.value_formatter;
            const formattedValue = formatValue(param.value, formatter);
            
            tooltip += `
              <div style="display: flex; align-items: center; margin-bottom: 4px;">
                <span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; border-radius: 50%; margin-right: 8px;"></span>
                <span style="margin-right: 8px;">${param.seriesName}:</span>
                <span style="font-weight: 600;">${formattedValue}</span>
              </div>
            `;
          });
          
          return tooltip;
        },
      },
    };
  }, [data, config]);

  return (
    <BaseChart
      options={chartOptions}
      height={height}
      width={width}
      chartType="line"
    />
  );
};

export default AreaChart;
